import { Preferences } from '@capacitor/preferences';
import { User } from 'firebase/auth';
import { hasEmailPassword, removeEmailPassword } from './accountCredentials';

export type SupportedAccountProvider = 'password' | 'google.com' | 'apple.com' | 'unknown';

export interface ISavedAccount {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string | null;
  isAdmin?: boolean;
  lastUsedAt: string;
  providers: SupportedAccountProvider[];
  hasQuickPasswordSwitch?: boolean;
}

const SAVED_ACCOUNTS_KEY = 'savedAccounts';
const MAX_SAVED_ACCOUNTS = 6;

function normalizeProviders(user: User | null | undefined): SupportedAccountProvider[] {
  const providerIds = new Set<SupportedAccountProvider>();

  user?.providerData?.forEach((provider) => {
    const providerId = provider.providerId as SupportedAccountProvider;
    if (providerId === 'password' || providerId === 'google.com' || providerId === 'apple.com') {
      providerIds.add(providerId);
    }
  });

  if (providerIds.size === 0) {
    if (user?.providerId === 'password' || user?.providerId === 'google.com' || user?.providerId === 'apple.com') {
      providerIds.add(user.providerId as SupportedAccountProvider);
    }
  }

  if (providerIds.size === 0) {
    providerIds.add('unknown');
  }

  return [...providerIds];
}

export async function getSavedAccounts(): Promise<ISavedAccount[]> {
  try {
    const { value } = await Preferences.get({ key: SAVED_ACCOUNTS_KEY });
    if (!value) return [];
    const parsed = JSON.parse(value) as ISavedAccount[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((account) => !!account?.uid && !!account?.email)
      .sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt));
  } catch {
    return [];
  }
}

export async function saveAccount(user: User, isAdmin: boolean): Promise<void> {
  if (!user.uid || !user.email) return;

  const providers = normalizeProviders(user);
  const hasQuickPasswordSwitch = providers.includes('password')
    ? await hasEmailPassword(user.email)
    : false;

  const nextAccount: ISavedAccount = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || user.email,
    photoURL: user.photoURL ?? null,
    isAdmin,
    lastUsedAt: new Date().toISOString(),
    providers,
    hasQuickPasswordSwitch,
  };

  const existing = await getSavedAccounts();
  const deduped = existing.filter((account) => account.uid !== nextAccount.uid && account.email !== nextAccount.email);
  const next = [nextAccount, ...deduped].slice(0, MAX_SAVED_ACCOUNTS);
  await Preferences.set({ key: SAVED_ACCOUNTS_KEY, value: JSON.stringify(next) });
}

export async function removeSavedAccount(uid: string): Promise<void> {
  const existing = await getSavedAccounts();
  const removed = existing.find((account) => account.uid === uid);
  const next = existing.filter((account) => account.uid !== uid);
  await Preferences.set({ key: SAVED_ACCOUNTS_KEY, value: JSON.stringify(next) });
  if (removed?.email) {
    await removeEmailPassword(removed.email);
  }
}
